// eslint-disable-next-line import/no-named-as-default
import posthog from 'posthog-js';
import type { CaptureResult, JsonType } from 'posthog-js';

import {
  clearAnonymousDistinctId,
  clearLegacyAnonymousDistinctId,
  persistAnonymousDistinctId,
  readAnonymousDistinctId
} from '@lib/analytics/posthog-identity';
import { readFlagsBootstrapCookie } from '@lib/analytics/posthog-flags-bootstrap';
import {
  discardPendingAnalyticsEvents,
  flushPendingAnalyticsEvents
} from '@lib/analytics/pending-events';

// Same-origin reverse proxy (see the /ingest rewrites in next.config.js) so
// ad blockers and VPNs cannot block ingestion or feature flag evaluation.
const POSTHOG_PROXY_PATH = '/ingest';

// Where the PostHog UI lives; needed for toolbar and dashboard links because
// api_host points at the proxy rather than PostHog itself.
const POSTHOG_UI_HOST = 'https://eu.posthog.com';

export const isPostHogConfigured = (): boolean => {
  return !!process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
};

/**
 * FFH-3561: the events that create or merge a PostHog person. `identify()`
 * still switches the distinct id to the member's id in the browser; dropping
 * its `$identify` stops the server linking that id to a person.
 */
const PERSON_PROFILE_EVENTS = new Set(['$identify', '$set']);

/**
 * FFH-3561: send every event without a person profile, so it bills at
 * PostHog's anonymous rate. After identify() the SDK marks events for person
 * processing, and this overrides it. That only saves money because member ids
 * have no person behind them (see buildMemberDistinctId): an event on an id
 * that already belongs to a person bills as identified whatever this says.
 */
export const sendWithoutPersonProfile = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event || PERSON_PROFILE_EVENTS.has(event.event)) return null;

  return {
    ...event,
    properties: { ...event.properties, $process_person_profile: false }
  };
};

/**
 * FFH-2368: the anonymous `distinct_id` for this page load, captured at init
 * before any `identify()` can have run, so it is anonymous by construction.
 * Read at consent time instead and a member whose Auth0 session resolves before
 * the CookieYes settle would have their member id written to the anonymous
 * cookie.
 */
let anonymousDistinctId: string | undefined;

/**
 * The flags PostHog last loaded this page load, as it reports them: only the
 * ones that are on. Kept because `posthog.reset()`, which setPostHogConsent
 * runs for every visitor without analytics consent, clears PostHog's flags
 * without loading them again or telling the hooks, and identify() (which
 * would reload them) is skipped without consent. Anything mounted after that
 * would otherwise read every flag as its default: launch flags off and kill
 * switches open.
 */
let lastLoadedFlagVariants: Record<string, string | boolean> | undefined;

/**
 * A flag's value as PostHog last loaded it this page load, or undefined
 * before it has loaded any. A flag missing from the load is off, as PostHog
 * itself reads it.
 */
export const readLastLoadedFlag = (flag: string): boolean | undefined =>
  lastLoadedFlagVariants === undefined
    ? undefined
    : Boolean(lastLoadedFlagVariants[flag]);

export const initPostHog = (): void => {
  if (typeof window === 'undefined') return;
  if (!isPostHogConfigured()) return;
  if (posthog.__loaded) return;

  const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
  if (!token) return;

  // Init runs unconditionally (no DNT/consent gate) so feature flags always
  // resolve — flag evaluation is a site dependency and must survive both DNT
  // and a pre-consent state. What consent gates is *capture*, *identify* and
  // *persistence*, handled by setPostHogConsent():
  //   - `opt_out_capturing_by_default: true` — no events captured until opt-in.
  //   - `persistence: 'memory'` — no cookies/localStorage before consent; the
  //     flags request uses an in-memory anonymous distinct id.
  //   - `advanced_disable_flags: false` — flags stay enabled.
  // `respect_dnt` is left at its default (false) on purpose: DNT must not
  // suppress flag evaluation.
  // FFH-2368: resume the previous anonymous identity when we have one. Bootstrap
  // is the only way in: persistence is 'memory' at this point, so the SDK cannot
  // read its own stored id, and it would otherwise mint a fresh one per load.
  // `isIdentifiedID: false` keeps the visitor anonymous; members are still
  // identified separately on their member id by PostHogProvider.
  clearLegacyAnonymousDistinctId();
  const storedAnonymousDistinctId = readAnonymousDistinctId();

  // Flag values evaluated by the middleware and delivered on the HTML
  // response (see lib/analytics/posthog-flags-bootstrap.ts). Bootstrapping
  // them makes flags correct at first paint (no flash of the hooks' default
  // values) and lets the init-time /flags request be skipped below. When the
  // cookie is missing or invalid this is undefined and the SDK behaves as
  // before: one /flags request per hard load.
  const flagsBootstrap = readFlagsBootstrapCookie();
  const hasBootstrapContent =
    Boolean(storedAnonymousDistinctId) || Boolean(flagsBootstrap);

  posthog.init(token, {
    api_host: POSTHOG_PROXY_PATH,
    ui_host: POSTHOG_UI_HOST,
    autocapture: false,
    // FFH-2370: pageviews ARE captured, but from the Next.js router
    // (PostHogPageviewTracker), not the SDK's history listener — the quiz
    // funnels step via raw pushState hash updates, which the SDK listener
    // would count as pageviews the same way they inflate Datadog RUM views.
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    // FFH-3561: `person_profiles` stays at its default, 'identified_only'.
    // 'never' would make identify() a no-op, and events would lose the member.
    before_send: sendWithoutPersonProfile,
    advanced_disable_flags: false,
    persistence: 'memory',
    opt_out_capturing_by_default: true,
    // Skip the init-time /flags request ONLY when bootstrapped values exist
    // to stand in for it; otherwise flags would sit undefined until a
    // member's identify() (and forever for anonymous visitors). Members still
    // reload flags on identify(), so per-user targeting would self-correct
    // for them; anonymous visitors live on the bootstrap until the next hard
    // load, which is why shared bootstrap values must stay valid for
    // everyone (see lib/analytics/posthog-flags-edge.ts).
    advanced_disable_feature_flags_on_first_load: Boolean(flagsBootstrap),
    ...(hasBootstrapContent
      ? {
          bootstrap: {
            ...(storedAnonymousDistinctId
              ? {
                  distinctID: storedAnonymousDistinctId,
                  isIdentifiedID: false
                }
              : {}),
            ...(flagsBootstrap
              ? {
                  featureFlags: flagsBootstrap.featureFlags,
                  featureFlagPayloads:
                    flagsBootstrap.featureFlagPayloads as Record<
                      string,
                      JsonType
                    >
                }
              : {})
          }
        }
      : {}),
    loaded: (ph) => {
      if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
        ph.debug();
      }
    }
  });

  anonymousDistinctId = storedAnonymousDistinctId ?? posthog.get_distinct_id();

  // Called at once with the bootstrapped flags, then after every load. A load
  // that failed keeps the last good flags rather than replacing them.
  posthog.onFeatureFlags((_flags, flagVariants, context) => {
    if (context?.errorsLoading) return;
    lastLoadedFlagVariants = flagVariants;
  });
};

/**
 * Mirror the anonymous id into the cookie once consent allows storage, so the
 * next hard load bootstraps the same person instead of minting a new one.
 */
const syncAnonymousIdentityCookie = (): void => {
  if (!anonymousDistinctId) return;
  persistAnonymousDistinctId(anonymousDistinctId);
};

/**
 * FFH-2368: reset identity on a genuine logout, then adopt the freshly minted
 * anonymous id as the stable one. Without re-seeding, the next hard load would
 * bootstrap the pre-logout cookie value and resurrect the identity we just
 * discarded.
 */
export const resetPostHogIdentityForLogout = (): void => {
  if (typeof window === 'undefined') return;
  if (!isPostHogConfigured()) return;
  if (!posthog.__loaded) return;

  posthog.reset();
  // reset() also clears the SDK's persisted opt-in flag (consent.reset()), and
  // the CookieYes consent value has not changed, so setPostHogConsent(true)
  // never re-fires. Without re-opting in here the rest of the session would
  // capture nothing despite granted consent. This function is only reached
  // with analytics consent granted (see the guard in PostHogProvider), and a
  // logout is not a consent change, so no `$opt_in` event here either
  // (FFH-2369).
  posthog.opt_in_capturing({ captureEventName: false });
  anonymousDistinctId = posthog.get_distinct_id();
  syncAnonymousIdentityCookie();
};

/**
 * Apply CookieYes analytics consent to PostHog. Flags keep working regardless;
 * this only toggles capture, persistence and (indirectly, via reset) identity.
 *
 * @param granted - true when the user has granted analytics consent.
 */
export const setPostHogConsent = (granted: boolean): void => {
  if (typeof window === 'undefined') return;
  if (!isPostHogConfigured()) return;
  if (!posthog.__loaded) return;

  if (granted) {
    // Persist identifiers and start capturing. Both calls are idempotent.
    posthog.set_config({ persistence: 'localStorage' });
    // FFH-2369: opt_in_capturing() captures an `$opt_in` event by default, and
    // this runs on every consented page load, so it was a material share of the
    // product analytics bill. It records a consent side-effect we already know
    // from CookieYes, appears in no insight, and is not a product event, so
    // `captureEventName: false` opts in without it. Volumes and cost share are
    // on FFH-2369.
    posthog.opt_in_capturing({ captureEventName: false });
    // FFH-2368: only now is storage permitted, so this is where the anonymous
    // id becomes durable. The value was captured at init, so it is the same one
    // the persistence switch above just wrote through to localStorage.
    syncAnonymousIdentityCookie();
    // FFH-2372: release events captured during the pre-settle window, now
    // that the SDK will actually send them. After opt-in, so they go out.
    flushPendingAnalyticsEvents((eventName, properties) =>
      posthog.capture(eventName, properties)
    );
  } else {
    if (posthog.has_opted_in_capturing()) {
      posthog.opt_out_capturing();
    }
    // Drop any stored identifiers and stop persisting new ones.
    posthog.set_config({ persistence: 'memory' });
    posthog.reset();
    // FFH-2368: a revoke must leave nothing behind, and the id the reset just
    // minted becomes the one we would persist on a later re-grant.
    clearAnonymousDistinctId();
    anonymousDistinctId = posthog.get_distinct_id();
    // FFH-2372: pre-settle events must not survive a denial; a later
    // re-grant would otherwise leak them out.
    discardPendingAnalyticsEvents();
  }
};

export { posthog };
